import { makeStyles } from '@material-ui/styles'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import { getUnderlayInfo } from 'core/containers/AuthContainer'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import { sessionActions } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { equals, keys, uniq, values, without } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty, noop } from 'utils/fp'
import { trackEvent } from 'utils/tracking'
import { v4 as uuid } from 'uuid'
import {
  createBlueprint,
  createHostNetworkConfigs,
  deleteHostNetworkConfig,
  getBlueprints,
  getHostNetworkConfigs,
  getResmgrRoleConfig,
  listHostNetworkConfigs,
  listResmgrHosts,
  updateBlueprint,
  updateHostNetworkConfig,
} from '../actions'
import { createVolumeType, addVolumeTypeTenant } from '../../storage/volume-types/actions'
import { resmgrHostsSelector } from '../selectors'
import BlueprintProgressBanner from './BlueprintProgressBanner'
import ClusterBlueprintBasicConfigs from './ClusterBlueprintBasicConfigs'
import ClusterBlueprintDefaults from './ClusterBlueprintDefaults'
import {
  BLUEPRINT_TABS,
  defaultImageLibraryLocation,
  defaultVmStoragePath,
  ImageLibraryStorageType,
} from './constants'
import { allSystemTrafficSelected, isCustomDriver, stringToBoolean } from './helpers'
import { getGpuModels, getPassthroughObject } from './host-network-configs/helpers'
import HostNetworkConfigurations from './host-network-configs/HostNetworkConfigurations'
import { Blueprint, BlueprintTypes, HostNetworkConfiguration } from './model'
import VolumeBackends from './volume-backend-configs/VolumeBackends'
import GetStartedWithBlueprint from './GetStartedWithBlueprint'
import { generateApiErrorPayload } from 'api-client/helpers'
import { ApiResponseErrorModel } from 'api-client/model'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { useVolumeBackends } from '../../storage/volume-types/VolumeBackendsProvider'

const useStyles = makeStyles((theme: Theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: theme.spacing(2),

    '& .card-body': {
      padding: theme.spacing(4, 4, 6),
    },
  },
  cardBody: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  formHeader: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  submitButtonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  infoText: {},
  submitWarning: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  warningColor: {
    color: theme.palette.yellow.main,
  },
  formsContainer: {
    display: 'grid',
    gap: theme.spacing(4),
  },
  switchDescription: {
    paddingLeft: 56,
  },
  explicitUpdate: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'flex-end',
  },
  errors: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  fieldLabel: {
    margin: '0px 4px',
  },
  switchSection: {
    marginLeft: 56,
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  nameField: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
}))

export interface VolumeBackend {
  type: string
  id: string
}
export type VolumeBackendConfig = {
  name: string
  driver: string
  customDriver: string
  volumeBackendName: string
  keyValues: Array<{ key: string; value: string }>
  volumeBackendId: string
}

export interface VolumeType {
  name: string
  description: string
  is_public: boolean
  volumeBackend: ''
  metadata: []
  tenants: []
}

export default function ClusterBlueprintPage() {
  const classes = useStyles({})
  const defaultParams = {
    name: '',
    physnetName: '',
    blueprintType: BlueprintTypes.MultiTenant,
    dnsDomainName: 'localdomain',
    imageLibraryLocation: defaultImageLibraryLocation,
    vmStoragePath: defaultVmStoragePath,
    networkingType: 'ovn',
    livenessCheckFrequency: 'default',
    enableDvr: true,
    enableVirtualNetworking: true,
    segmentationTechnology: 'vlan',
    segmentationRange: '',
    hostNetworkConfigs: [],
    volumeBackends: [] as VolumeBackend[],
    volumeBackendConfigs: [] as VolumeBackendConfig[],
    volumeTypes: [] as VolumeType[],
    newVolumeTypeName: '',
    imageLibrarySharedStorage: false,
    instanceSharedStorage: false,
    vncFloatingIp: '',
    // segmentationId: '',
    // physNetMap: [{ interface: '', range: '', label: 'vm-net' }],
  }
  const dispatch = useDispatch()
  const { history, location } = useReactRouter()

  const { params, updateParams, getParamsUpdater, paramsUpdated, setParamsUpdated } = useParams(
    defaultParams,
  )

  const searchParams = new URLSearchParams(location.search)
  const activeTabParam = searchParams.get('activeTab')

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [historyLocation, setHistoryLocation] = useState(null)
  const [blueprintSaved, setBlueprintSaved] = useState(false)
  const [saveFailure, setSaveFailure] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [currentHostNetworkConfigs, setCurrentHostNetworkConfigs] = useState<
    HostNetworkConfiguration[]
  >([])
  const [showGetStartedModal, setShowGetStartedModal] = useState(false)
  const [noBlueprint, setNoBlueprint] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const [currentBlueprintName, setCurrentBlueprintName] = useState('')
  const [currentBlueprint, setCurrentBlueprint] = useState<Blueprint>({} as Blueprint)
  // const [usedVolumeBackends, setUsedVolumeBackends] = useState([])
  const submitFuncRef = useRef(null)
  const resetFuncRef = useRef(null)
  const [activeTab, setActiveTab] = useState(activeTabParam || BLUEPRINT_TABS.BASIC_CONFIGS.value)
  const [errorIndexes, setErrorIndexes] = useState<number[]>([])
  const [imageLibraryStorageType, setImageLibraryStorageType] = useState<ImageLibraryStorageType>(
    ImageLibraryStorageType.LOCAL_FILESYSTEM,
  )

  // Distinguish between a real submit (Save Blueprint) and validation-only runs (e.g. tab change)
  const isSubmitActionRef = useRef(false)

  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts)
  const { reload: reloadHostNetworkConfigs } = useListAction(listHostNetworkConfigs)
  const hosts = useSelector(resmgrHostsSelector)
  const hasMultipleImageLibraryHosts = useMemo(() => {
    return (hosts?.filter((host) => host?.roles?.includes('image-library'))?.length ?? 0) >= 2
  }, [hosts])
  const hypervisorAuthorized = useMemo(() => {
    return hosts?.some((host) => host.roles?.includes('hypervisor'))
  }, [hosts])
  const imageLibraryAuthorized = useMemo(() => {
    return hosts?.some((host) => host.roles?.includes('image-library'))
  }, [hosts])
  const loading = !loaded || loadingResmgrHosts

  // Calculate completion status for progress banner
  const blueprintProgress = useMemo(() => {
    if (isNilOrEmpty(params)) {
      return {
        completedSections: 0,
        totalSections: 4,
        isComplete: false,
        hasBasicConfig: false,
        hasHostConfig: false,
        hasStorageConfig: false,
        hasClusterDefaults: false,
      }
    }
    const hasBasicConfig =
      !!params?.name &&
      !!params?.dnsDomainName &&
      !!params?.segmentationTechnology &&
      !!params?.segmentationRange
    const hasHostConfig =
      params?.hostNetworkConfigs.length > 0 && allSystemTrafficSelected(params?.hostNetworkConfigs)
    const hasStorageConfig = Object.keys(params?.volumeBackends).length > 0
    const hasClusterDefaults =
      !!params?.imageLibraryLocation &&
      !!params?.vmStoragePath &&
      !!currentBlueprint?.imageLibraryStorage &&
      !!currentBlueprint?.vmStorage

    const completedSections = [
      hasBasicConfig,
      hasHostConfig,
      hasStorageConfig,
      hasClusterDefaults,
    ].filter(Boolean).length

    return {
      completedSections,
      totalSections: 4,
      isComplete: completedSections === 4,
      hasBasicConfig,
      hasHostConfig,
      hasStorageConfig,
      hasClusterDefaults,
    }
  }, [params, currentBlueprint])

  // Show confirmation dialog when the blueprint changes are not saved
  const unsavedChanges = useRef<boolean>(false)
  useEffect(() => {
    const checkForUnsavedChanges = history.block((location, action) => {
      if (
        unsavedChanges?.current &&
        location?.pathname !== routeManager.routes.openstack.clusterBlueprint.path()
      ) {
        setHistoryLocation(location?.pathname)
        setShowUnsavedChangesModal(true)
        return false
      }
      return true
    })

    return () => {
      checkForUnsavedChanges()
    }
  }, [unsavedChanges])

  // Set unsavedChanges to true and Reset blueprint saved on any updates
  useEffect(() => {
    if (!paramsUpdated) return
    unsavedChanges.current = true
    setBlueprintSaved(false)
  }, [paramsUpdated])

  const { usedVolumeBackends } = useVolumeBackends()

  // useEffect(() => {
  //   const storageHosts = hosts?.filter((host) => host.roles?.includes('persistent-storage'))
  //   if (!storageHosts?.length) {
  //     return
  //   }
  //   const getUsedVolumeBackends = async () => {
  //     const results: any = await Promise.allSettled(
  //       storageHosts.map((host) => getResmgrRoleConfig({ role: 'pf9-cindervolume-config', host })),
  //     )
  //     const values = results?.map((result) => result?.value)
  //     const backends = values?.reduce((accum, value) => {
  //       return [...accum, ...keys(value?.backends)]
  //     }, [])
  //     const uniqueBackends = uniq(backends)
  //     setUsedVolumeBackends(uniqueBackends)
  //   }
  //   getUsedVolumeBackends()
  // }, [hosts])

  const resetImageLibraryStorageType = useCallback((blueprintSettings: Blueprint) => {
    setImageLibraryStorageType(
      blueprintSettings?.imageLibraryStorage?.startsWith('/')
        ? blueprintSettings?.imageLibrarySharedStorage
          ? ImageLibraryStorageType.SHARED_FILESYSTEM
          : ImageLibraryStorageType.LOCAL_FILESYSTEM
        : ImageLibraryStorageType.VOLUME_TYPE,
    )
  }, [])

  // Flag to check if the user is setting the blueprint first time
  const isUserSavingBlueprintFirstTime = useRef<boolean>(false)

  const querySettings = async () => {
    let blueprints = []
    try {
      blueprints = (await getBlueprints()) as any[]
    } catch (error) {
      setNoBlueprint(true)
      setShowGetStartedModal(true)
      setLoaded(true)
      isUserSavingBlueprintFirstTime.current = true
      return
    }

    // Get the first blueprint
    const blueprintSettings = blueprints?.[0]
    if (!blueprintSettings) {
      setLoaded(true)
      setNoBlueprint(true)
      setShowGetStartedModal(true)
      isUserSavingBlueprintFirstTime.current = true
      return
    }

    setCurrentBlueprintName(blueprintSettings?.name)
    setCurrentBlueprint(blueprintSettings)
    const hostNetworkConfigurations: any = await getHostNetworkConfigs()

    // set session data for underlay network & virtual networking
    const virtualNetworking = !!blueprintSettings?.virtualNetworking?.enabled
    const underlayNetwork = getUnderlayInfo(blueprintSettings, hostNetworkConfigurations)
    dispatch(
      sessionActions.updateSession({
        virtualNetworking,
        underlayNetwork,
      }),
    )

    // Only get the host configs for the current blueprint
    const hostNetworkConfigs = hostNetworkConfigurations
      ?.filter((networkConfig) => networkConfig?.clusterName === blueprintSettings?.name)
      ?.map((networkConfig) => {
        const networkInterfacesWithLabels = values(networkConfig?.networkLabels)
        const otherInterfaces = [
          networkConfig?.hostLivenessInterface,
          networkConfig?.imagelibInterface,
          networkConfig?.mgmtInterface,
          networkConfig?.tunnelingInterface,
          networkConfig?.vmConsoleInterface,
        ]
        const uniqueInterfaces = uniq([...networkInterfacesWithLabels, ...otherInterfaces]).filter(
          (iface) => !!iface,
        )
        const networkLabelKeys = keys(networkConfig.networkLabels)
        const networkLabels = networkLabelKeys?.map((key) => ({
          key: key,
          value: networkConfig.networkLabels[key],
        }))
        return {
          name: networkConfig?.name,
          networkInterfaces: uniqueInterfaces.map((iface) => {
            return {
              physnet: iface,
              physnetName: iface,
              physicalNetworkLabel: networkLabels.find((pair) => pair.value === iface)?.key,
              hostLiveness: networkConfig.hostLivenessInterface === iface,
              imageLibrary: networkConfig.imagelibInterface === iface,
              management: networkConfig.mgmtInterface === iface,
              tunnel: networkConfig.tunnelingInterface === iface,
              vmConsole: networkConfig.vmConsoleInterface === iface,
            }
          }),
          id: networkConfig?.id,
          gpuPciPassthroughs: networkConfig?.gpuPci?.map((pci) => pci.name),
          srIOVDevice: networkConfig?.gpuPci?.some((pci) => pci.deviceType === 'type-PF'),
        }
      })
    setCurrentHostNetworkConfigs(hostNetworkConfigs)
    const volumeBackends = keys(blueprintSettings?.storageBackends)
    const volumeBackendsWithId = volumeBackends.map((type: string) => {
      return {
        type,
        id: uuid(),
      }
    })
    const volumeBackendConfigs = volumeBackendsWithId.reduce((accum, { type, id }) => {
      const configs = blueprintSettings?.storageBackends?.[type]
      const configNames = keys(configs)
      const wizardConfigs = configNames.map((name) => {
        const configKeys = keys(configs[name]?.config)
        const keyValues = configKeys.map((key) => ({
          key: key,
          value: String(configs[name]?.config?.[key]),
        }))
        return {
          name: name,
          driver: isCustomDriver(configs[name]?.driver) ? 'Custom' : configs[name]?.driver,
          customDriver: isCustomDriver(configs[name]?.driver) ? configs[name]?.driver : '',
          volumeBackendName: type,
          keyValues: keyValues,
          volumeBackendId: id,
        }
      })
      return [...accum, ...wizardConfigs]
    }, [])

    // Detect storage type based on imageLibraryLocation value
    if (blueprintSettings?.imageLibraryStorage) {
      resetImageLibraryStorageType(blueprintSettings)
    }

    updateParams({
      name: blueprintSettings?.name || defaultParams.name,
      networkingType: blueprintSettings?.networkingType || defaultParams.networkingType,
      //As mentioned in tracker We can expose these in the future Commenting out - https://platform9.atlassian.net/browse/PCD-3968
      // enableDvr:
      //   blueprintSettings?.enableDistributedRouting === false ? false : defaultParams.enableDvr,
      // enableVirtualNetworking:
      //   blueprintSettings?.virtualNetworking?.enabled === false
      //     ? false
      //     : defaultParams.enableVirtualNetworking,
      imageLibrarySharedStorage:
        blueprintSettings?.imageLibrarySharedStorage || defaultParams.imageLibrarySharedStorage,
      instanceSharedStorage:
        blueprintSettings?.instanceSharedStorage || defaultParams.instanceSharedStorage,
      dnsDomainName: blueprintSettings?.dnsDomainName || defaultParams.dnsDomainName,
      segmentationTechnology:
        blueprintSettings?.virtualNetworking?.underlayType || defaultParams.segmentationTechnology,
      segmentationRange:
        blueprintSettings?.virtualNetworking?.vnidRange?.split(':')?.join('-') ||
        defaultParams.segmentationRange,
      imageLibraryLocation:
        blueprintSettings?.imageLibraryStorage || defaultParams.imageLibraryLocation,
      vmStoragePath: blueprintSettings?.vmStorage || defaultParams.vmStoragePath,
      hostNetworkConfigs: hostNetworkConfigs || defaultParams.hostNetworkConfigs,
      volumeBackends: (volumeBackendsWithId || defaultParams.volumeBackends) as VolumeBackend[],
      volumeBackendConfigs: (volumeBackendConfigs ||
        defaultParams.volumeBackendConfigs) as VolumeBackendConfig[],
      vncFloatingIp: blueprintSettings?.vncFloatingIp || defaultParams.vncFloatingIp,
    })
    setParamsUpdated(false)
    setLoaded(true)
  }

  useEffect(() => {
    querySettings()
  }, [])

  // Handle storage type change
  const handleImageLibraryStorageTypeChange = useCallback(
    (value: ImageLibraryStorageType) => {
      setImageLibraryStorageType((prevValue) => {
        updateParams({
          imageLibraryLocation:
            value === ImageLibraryStorageType.VOLUME_TYPE && value !== prevValue
              ? ''
              : currentBlueprint?.imageLibraryStorage || defaultImageLibraryLocation,
        })
        return value
      })
    },
    [updateParams, currentBlueprint],
  )

  const {
    update: createVolumeTypeFn,
    updating,
    error,
    reset: resetCreateVolumeType,
  } = useUpdateAction(createVolumeType)
  const { update: addVolumeTypeTenantFn, reset: resetAddTenant } = useUpdateAction(
    addVolumeTypeTenant,
  )

  const submitBlueprint = useCallback(() => {
    // When running validations for tab changes, do not actually submit
    if (!isSubmitActionRef.current) {
      return
    }
    if (isReadonlyUser()) return
    // Todo: add validation for all the checkboxes being selected for traffic
    if (!allSystemTrafficSelected(params.hostNetworkConfigs)) {
      setActiveTab(BLUEPRINT_TABS.HOST_CONFIGS.value)
      history.replace({
        search: `?activeTab=${BLUEPRINT_TABS.HOST_CONFIGS.value}`,
      })
      return
    }

    const asyncCalls = async () => {
      setSubmitting(true)
      setErrorIndexes([])
      // Scroll to top so we can see the loading componentd
      document.querySelector('.content-main').scrollTo(0, 0)
      let errorOccurred = false
      const errMessages = []

      // Create volume types if any are defined
      if (params.volumeTypes && params.volumeTypes.length > 0) {
        const volumeTypeResults = await Promise.allSettled(
          params.volumeTypes.map(async (volumeType) => {
            // Remove tenants from volume type object before sending to API
            const { tenants, ...volumeTypeData } = volumeType

            // Wrap the volume type object in the volume_type key as required by the API
            const body = {
              volume_type: volumeTypeData,
            }

            // Create the volume type
            const createResponse = await createVolumeTypeFn({ body })

            // Check if the creation was successful
            if (!createResponse.success) {
              throw new Error(createResponse.error || 'Failed to create volume type')
            }

            const volumeTypeId = createResponse?.response?.id

            // If the volume type is not public and has tenants, add tenant access
            if (
              !volumeTypeData['os-volume-type-access:is_public'] &&
              tenants?.length > 0 &&
              volumeTypeId
            ) {
              const tenantResults = await Promise.allSettled(
                tenants.map((tenant: any) => {
                  const tenantBody = {
                    addProjectAccess: {
                      project: tenant.id,
                    },
                  }
                  return addVolumeTypeTenantFn({ id: volumeTypeId, body: tenantBody })
                }),
              )

              // Check for tenant assignment failures
              const failedTenants = tenantResults.filter((result) => result.status === 'rejected')
              if (failedTenants.length > 0) {
                throw new Error(`Failed to assign tenants to volume type ${volumeTypeData.name}`)
              }
            }

            return createResponse
          }),
        )

        // Check for volume type creation failures
        const failedVolumeTypes = volumeTypeResults
          .map((result, index) => ({
            index,
            ...result,
          }))
          .filter((result) => result.status === 'rejected')

        if (failedVolumeTypes.length > 0) {
          errorOccurred = true
          for (const value of failedVolumeTypes) {
            const volumeTypeName = params.volumeTypes[value.index]?.name || 'Unknown'
            const errorMessage =
              generateApiErrorPayload(
                value.reason,
                '',
                `Volume Type ${volumeTypeName} failed to create`,
              )?.message || `Volume Type ${volumeTypeName} failed to create`
            errMessages.push(errorMessage)
            trackEvent(`Cluster Blueprint - Volume Type failed to create`, {
              volumeTypeName,
              errorMessage,
            })
          }
        } else {
          // Clear the volumeTypes from params after successful creation
          updateParams({ volumeTypes: [] })
        }
      }

      const volumeBackends = params.volumeBackendConfigs.reduce((accum, config) => {
        if (accum[config.volumeBackendName]) {
          return {
            ...accum,
            [config.volumeBackendName]: {
              ...accum[config.volumeBackendName],
              [config.name]: {
                driver: isCustomDriver(config.driver) ? config.customDriver : config.driver,
                config: config.keyValues.reduce((accum, keyValues) => {
                  return {
                    ...accum,
                    [keyValues.key]: ['true', 'false'].includes(keyValues.value?.toLowerCase())
                      ? stringToBoolean(keyValues.value)
                      : keyValues.value,
                  }
                }, {}),
              },
            },
          }
        } else {
          return {
            ...accum,
            [config.volumeBackendName]: {
              [config.name]: {
                driver: isCustomDriver(config.driver) ? config.customDriver : config.driver,
                config: config.keyValues.reduce((accum, keyValues) => {
                  return {
                    ...accum,
                    [keyValues.key]: ['true', 'false'].includes(keyValues.value?.toLowerCase())
                      ? stringToBoolean(keyValues.value)
                      : keyValues.value,
                  }
                }, {}),
              },
            },
          }
        }
      }, {})

      //Image library storage type prechecks
      let imageLibraryLocationTemp = params.imageLibraryLocation
      let imageLibrarySharedStorageTemp =
        imageLibraryStorageType === ImageLibraryStorageType.VOLUME_TYPE ||
        imageLibraryStorageType === ImageLibraryStorageType.SHARED_FILESYSTEM
      if (
        imageLibraryStorageType === ImageLibraryStorageType.VOLUME_TYPE &&
        !params.imageLibraryLocation
      ) {
        imageLibraryLocationTemp = currentBlueprint?.imageLibraryStorage
        imageLibrarySharedStorageTemp = currentBlueprint?.imageLibrarySharedStorage
        resetImageLibraryStorageType(currentBlueprint)
      }

      const blueprintConfigBody = {
        name: params.name,
        networkingType: params.networkingType,
        // backend was sending a string
        enableDistributedRouting: Boolean(params.enableDvr),
        dnsDomainName: params.dnsDomainName,
        virtualNetworking: {
          enabled: params.enableVirtualNetworking,
          underlayType: params.segmentationTechnology,
          vnidRange: params.segmentationRange?.split('-')?.join(':'),
        },
        imageLibraryStorage: imageLibraryLocationTemp,
        vmStorage: params.vmStoragePath,
        storageBackends: volumeBackends,
        imageLibrarySharedStorage: imageLibrarySharedStorageTemp,
        instanceSharedStorage: params.instanceSharedStorage,
        vncFloatingIp: params.vncFloatingIp ? params.vncFloatingIp : null,
      }
      const gpuModels = await getGpuModels()
      const hostConfigs = params.hostNetworkConfigs.map((config) => {
        const configProps = config.networkInterfaces.reduce(
          (accum, iface) => {
            const ifaceProps: any = {}
            if (iface.management) {
              ifaceProps.mgmtInterface = iface.physnet
            }
            if (iface.vmConsole) {
              ifaceProps.vmConsoleInterface = iface.physnet
            }
            if (iface.hostLiveness) {
              ifaceProps.hostLivenessInterface = iface.physnet
            }
            if (iface.tunnel) {
              ifaceProps.tunnelingInterface = iface.physnet
            }
            if (iface.imageLibrary) {
              ifaceProps.imagelibInterface = iface.physnet
            }
            return iface.physicalNetworkLabel
              ? {
                  ...accum,
                  ...ifaceProps,
                  networkLabels: {
                    ...accum.networkLabels,
                    [iface.physicalNetworkLabel]: iface.physnet,
                  },
                }
              : {
                  ...accum,
                  ...ifaceProps,
                }
          },
          { networkLabels: {} },
        )
        return {
          name: config.name,
          clusterName: params.name,
          id: config.id,
          gpuPci: config?.gpuPciPassthroughs?.map((passthrough) =>
            getPassthroughObject(passthrough, config?.srIOVDevice, gpuModels),
          ),
          ...configProps,
        }
      })
      trackEvent(`Cluster Blueprint - Submitted`)

      const blueprintAction = noBlueprint ? createBlueprint : updateBlueprint
      const blueprintResponse = await blueprintAction({
        name: currentBlueprintName ? currentBlueprintName : params.name,
        body: blueprintConfigBody,
      }).catch(
        (err) =>
          new Error(
            generateApiErrorPayload(
              (err as unknown) as ApiResponseErrorModel,
              'Blueprint failed to save',
            )?.message || 'Blueprint failed to save',
          ),
      )

      if (blueprintResponse instanceof Error) {
        // Blueprint only seems to give 500 errors which don't
        // give any info anyways, so just show generic error
        errorOccurred = true
        setSaveFailure(errorOccurred)
        setErrorMessages([blueprintResponse?.message])
        setSubmitting(false)
        trackEvent(`Cluster Blueprint - Failed to Save`, { response: blueprintResponse })
        unsavedChanges.current = false
        return
      } else {
        setNoBlueprint(false)
      }

      const networkChangeOccurred = !equals(
        {
          enableDistributedRouting: currentBlueprint?.enableDistributedRouting,
          dnsDomainName: currentBlueprint?.dnsDomainName,
          virtualNetworking: {
            enabled: currentBlueprint?.virtualNetworking?.enabled,
            underlayType: currentBlueprint?.virtualNetworking?.underlayType,
            vnidRange: currentBlueprint?.virtualNetworking?.vnidRange,
          },
        },
        {
          enableDistributedRouting: blueprintConfigBody?.enableDistributedRouting,
          dnsDomainName: blueprintConfigBody?.dnsDomainName,
          virtualNetworking: {
            enabled: blueprintConfigBody?.virtualNetworking?.enabled,
            underlayType: blueprintConfigBody?.virtualNetworking?.underlayType,
            vnidRange: blueprintConfigBody?.virtualNetworking?.vnidRange,
          },
        },
      )

      const currentHostConfigIds = currentHostNetworkConfigs.map((config) => config.id)
      const hostConfigsToUpdate = hostConfigs.filter((config) => {
        if (!currentHostConfigIds.includes(config.id)) {
          return false
        }
        if (networkChangeOccurred) {
          return true
        }
        const currentConfig = currentHostNetworkConfigs.find((c) => c.id === config.id)
        const newConfig = params.hostNetworkConfigs.find((c) => c.id === config.id)
        return !equals(currentConfig, newConfig)
      })
      const hostConfigsToAdd = hostConfigs.filter((config) => {
        return !currentHostConfigIds.includes(config.id)
      })
      const hostConfigIds = hostConfigs.map((config) => config.id).filter((id) => !!id)
      const hostConfigsToDelete = without(hostConfigIds, currentHostConfigIds)

      if (hostConfigsToAdd.length) {
        const createResponse = await createHostNetworkConfigs(hostConfigsToAdd).catch((err) => {
          return new Error(err)
        })
        if (createResponse instanceof Error) {
          const errorMessage = generateApiErrorPayload(
            (createResponse as unknown) as ApiResponseErrorModel,
            '',
            `Error creating new host configurations`,
          )?.message
          errMessages.push(errorMessage)
          errMessages.push('Error creating new host configurations')
          trackEvent(`Cluster Blueprint - Host Network Configuration(s) failed to create`, {
            response: createResponse,
          })
          errorOccurred = true
        }
      }
      const updateResults = await Promise.allSettled(
        hostConfigsToUpdate.map((config) => {
          return updateHostNetworkConfig({
            id: config.id,
            body: {
              ...config,
              id: undefined,
            },
          })
        }),
      )
      const deleteResults = await Promise.allSettled(
        hostConfigsToDelete.map((id) => {
          return deleteHostNetworkConfig({ id })
        }),
      )

      const rejectedUpdateIndexes = updateResults
        ?.map((result, index) => ({
          index,
          ...result,
        }))
        ?.filter((result) => result.status === 'rejected')
      const rejectedDeleteIndexes = deleteResults
        ?.map((result, index) => ({
          index,
          ...result,
        }))
        ?.filter((result) => result.status === 'rejected')

      if (rejectedUpdateIndexes?.length || rejectedDeleteIndexes?.length) {
        errorOccurred = true
      }

      for (const value of rejectedUpdateIndexes) {
        const configName = hostConfigsToUpdate[value.index].name
        const errorMessage = generateApiErrorPayload(
          value.reason,
          '',
          `Host Network Configuration ${configName} failed to update`,
        )?.message
        errMessages.push(errorMessage)
        trackEvent(`Cluster Blueprint - Host Network Configuration failed to update`, {
          errorMessage,
        })
      }
      for (const value of rejectedDeleteIndexes) {
        const configName = hostConfigsToDelete[value.index]
        const errorMessage = generateApiErrorPayload(
          value.reason,
          '',
          `Host Network Configuration ${configName} failed to delete`,
        )?.message
        errMessages.push(errorMessage)
        trackEvent(`Cluster Blueprint - Host Network Configuration failed to delete`, {
          errorMessage,
        })
      }

      querySettings()
      // If there are no errors, set blueprint saved
      if (errMessages.length === 0) {
        setBlueprintSaved(true)
      }
      setParamsUpdated(false)
      setSubmitting(false)
      reloadHostNetworkConfigs(true, true)
      setSaveFailure(errorOccurred)
      setErrorMessages(errMessages)
      unsavedChanges.current = false
      resetCreateVolumeType()
      resetAddTenant()
      // Reset validation state after successful save
      // Reset validation state after successful save
      if (!errorOccurred && resetFuncRef.current) {
        resetFuncRef.current.resetValidationState?.()
      }
      trackEvent(`Cluster Blueprint - Finished`, { errorMessages: errMessages })
    }
    asyncCalls()
  }, [
    params,
    currentHostNetworkConfigs,
    noBlueprint,
    currentBlueprint,
    imageLibraryStorageType,
    resetImageLibraryStorageType,
  ])

  const setSubmitFuncRef = (handleSubmit) => {
    submitFuncRef.current = { handleSubmit }
  }

  const setResetFuncRef = (resetValidationState) => {
    resetFuncRef.current = { resetValidationState }
  }

  const onValidationFail = useCallback(
    ({ step, results }) => {
      const failedFields = results.filter((prop) => prop.result === false)

      const failedFieldNames = failedFields.map((prop) => prop.name || prop.key)
      trackEvent(`Cluster Blueprint - Validation Failed`, { failedFields: failedFieldNames })

      // Check for failed host network configs and set error indexes
      if (
        activeTab === BLUEPRINT_TABS.HOST_CONFIGS.value ||
        activeTab === BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value
      ) {
        const failedHostConfigIndexes = failedFields
          .map((prop) => prop.key || prop.name)
          .filter(
            (key) =>
              typeof key === 'string' &&
              (key.startsWith('hostNetworkConfigs.') || key.startsWith('volumeBackends.')),
          )
          .map((key) => {
            const parts = key.split('.')
            const index = parseInt(parts[1], 10)
            return Number.isNaN(index) ? null : index
          })
          .filter((idx) => idx !== null)

        if (failedHostConfigIndexes.length) {
          setErrorIndexes(Array.from(new Set(failedHostConfigIndexes as number[])))
        }
      }

      // If this is a real submit action, redirect to the tab with validation errors
      if (isSubmitActionRef.current) {
        const getTabForField = (fieldName: string): string | null => {
          // Host Network Configs tab
          if (fieldName.startsWith('hostNetworkConfigs.')) {
            return BLUEPRINT_TABS.HOST_CONFIGS.value
          }
          // Persistent Storage Connectivity tab
          if (
            fieldName.startsWith('volumeBackends.') ||
            fieldName.startsWith('volumeBackendConfigs.')
          ) {
            return BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value
          }
          // Cluster Defaults tab
          if (
            fieldName === 'imageLibraryLocation' ||
            fieldName === 'vmStoragePath' ||
            fieldName === 'imageLibrarySharedStorage' ||
            fieldName === 'instanceSharedStorage' ||
            fieldName === 'vncFloatingIp'
          ) {
            return BLUEPRINT_TABS.CLUSTER_DEFAULTS.value
          }
          // Basic Configs tab (default for name, dnsDomainName, segmentationRange, etc.)
          return BLUEPRINT_TABS.BASIC_CONFIGS.value
        }

        // Find the first tab with errors
        const tabsWithErrors = new Set(
          failedFieldNames.map((fieldName) => getTabForField(fieldName)).filter(Boolean),
        )

        // Priority order: Basic Configs -> Host Configs -> Storage -> Cluster Defaults
        const tabPriority = [
          BLUEPRINT_TABS.BASIC_CONFIGS.value,
          BLUEPRINT_TABS.HOST_CONFIGS.value,
          BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value,
          BLUEPRINT_TABS.CLUSTER_DEFAULTS.value,
        ]

        const firstErrorTab = tabPriority.find((tab) => tabsWithErrors.has(tab))

        if (firstErrorTab && firstErrorTab !== activeTab) {
          setActiveTab(firstErrorTab)
          history.replace({
            search: `?activeTab=${firstErrorTab}`,
          })
          trackEvent(`Cluster Blueprint - Redirected to Tab with Errors`, { tab: firstErrorTab })
        }
      }
    },
    [activeTab, history],
  )

  const handleSaveClick = useCallback(async () => {
    if (!submitFuncRef.current?.handleSubmit) return
    // Mark this as a real submit so submitBlueprint side-effects execute
    isSubmitActionRef.current = true
    try {
      await submitFuncRef.current.handleSubmit()
    } finally {
      isSubmitActionRef.current = false
    }
  }, [])

  const handleTabChange = useCallback(
    async (nextTab: string) => {
      if (nextTab === activeTab) return

      // Reset validation state when switching tabs
      if (resetFuncRef.current) {
        resetFuncRef.current.resetValidationState?.()
      }

      history.replace({
        search: `?activeTab=${nextTab}`,
      })
      setErrorIndexes([])
      setActiveTab(nextTab)
      trackEvent(`Cluster Blueprint - Tab Change`, { tab: nextTab })
    },
    [activeTab],
  )

  return (
    <>
      <Progress
        message={
          submitting
            ? 'Updating Blueprint... This may take a few minutes. Please wait until the spinner stops to ensure that all settings are applied.'
            : 'Loading'
        }
        loading={loading || !loaded || submitting}
      >
        <DocumentMeta title="Cluster Blueprint" bodyClasses={['form-view']} />
        {showGetStartedModal ? (
          <GetStartedWithBlueprint
            onClick={() => {
              history.replace({
                search: `?activeTab=${BLUEPRINT_TABS.BASIC_CONFIGS.value}`,
              })
              setShowGetStartedModal(false)
            }}
          />
        ) : (
          <>
            <ConfirmationDialog
              text="Any unsaved changes in the Cluster Blueprint will be lost?"
              cancelText="Save Blueprint"
              confirmText="Proceed Anyway"
              onConfirm={() => {
                unsavedChanges.current = false
                setShowUnsavedChangesModal(false)
                history.push(historyLocation)
              }}
              onCancel={() => {
                setShowUnsavedChangesModal(false)
                submitFuncRef.current.handleSubmit()
              }}
              open={showUnsavedChangesModal}
              loading={submitting}
              onBackdropClick={noop}
            />
            <div className={classes.formsContainer}>
              <ValidatedForm
                classes={{ root: classes.validatedFormContainer }}
                elevated={false}
                onSubmit={submitBlueprint}
                triggerSubmit={setSubmitFuncRef}
                triggerReset={setResetFuncRef}
                onValidationFail={onValidationFail}
                formActions={null}
                noEnter // Without this, pressing enter in other modals would make the form submit for some reason
              >
                <BlueprintProgressBanner
                  activeTab={activeTab}
                  hasBasicConfig={blueprintProgress.hasBasicConfig}
                  hasHostConfig={blueprintProgress.hasHostConfig}
                  hasStorageConfig={blueprintProgress.hasStorageConfig}
                  hasClusterDefaults={blueprintProgress.hasClusterDefaults}
                  showSaveButton={!isReadonlyUser()}
                  blueprintSaved={blueprintSaved}
                  errorMessages={errorMessages}
                  onSave={handleSaveClick}
                  handleTabChange={handleTabChange}
                  isUserSavingBlueprintFirstTime={isUserSavingBlueprintFirstTime.current}
                />
                <Tabs activeTab={activeTab} setActiveTab={handleTabChange} keepTabsInView={true}>
                  <Tab
                    value={BLUEPRINT_TABS.BASIC_CONFIGS.value}
                    label={BLUEPRINT_TABS.BASIC_CONFIGS.label}
                  >
                    <ClusterBlueprintBasicConfigs
                      params={params}
                      getParamsUpdater={getParamsUpdater}
                      currentBlueprintName={currentBlueprintName}
                      hypervisorAuthorized={hypervisorAuthorized}
                    />
                  </Tab>
                  <Tab
                    value={BLUEPRINT_TABS.HOST_CONFIGS.value}
                    label={BLUEPRINT_TABS.HOST_CONFIGS.label}
                  >
                    <HostNetworkConfigurations
                      existingHostNetworkConfigs={currentHostNetworkConfigs}
                      params={params}
                      updateParams={updateParams}
                      hostConfigErrorIndexes={errorIndexes}
                      setHostConfigErrorIndexes={setErrorIndexes}
                    />
                  </Tab>
                  <Tab
                    value={BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value}
                    label={BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.label}
                  >
                    <VolumeBackends
                      params={params}
                      updateParams={updateParams}
                      getParamsUpdater={getParamsUpdater}
                      usedVolumeBackends={usedVolumeBackends}
                      existingVolumeBackends={currentBlueprint?.storageBackends}
                      storageVolumeErrorIndexes={errorIndexes}
                      setStorageVolumeErrorIndexes={setErrorIndexes}
                    />
                  </Tab>
                  <Tab
                    value={BLUEPRINT_TABS.CLUSTER_DEFAULTS.value}
                    label={BLUEPRINT_TABS.CLUSTER_DEFAULTS.label}
                  >
                    <ClusterBlueprintDefaults
                      params={params}
                      getParamsUpdater={getParamsUpdater}
                      imageLibraryAuthorized={imageLibraryAuthorized}
                      hypervisorAuthorized={hypervisorAuthorized}
                      imageLibraryStorageType={imageLibraryStorageType}
                      handleImageLibraryStorageTypeChange={handleImageLibraryStorageTypeChange}
                    />
                  </Tab>
                </Tabs>
              </ValidatedForm>
            </div>
          </>
        )}
      </Progress>
    </>
  )
}
